import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface ReportMetric {
  ID?: number;
  report_id?: number;
  metric_name: string;
  metric_code: string;
  value: string;
  numeric_value: number;
  unit: string;
  reference_min: number;
  reference_max: number;
  reference_range: string;
  status: string;
  category: string;
  sort_order: number;
}

export interface ReportPrescription {
  ID?: number;
  report_id?: number;
  medication_name: string;
  generic_name: string;
  dosage: string;
  frequency: string;
  duration: string;
  route: string;
  instructions: string;
  confidence: number;
  sort_order: number;
}

export interface Report {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  customer_id: number;
  appointment_id: number;
  lab_id: number;
  lab_name: string;
  test_name: string;
  test_code: string;
  category: string;
  report_date: string;
  sample_date: string;
  status: string;
  summary: string;
  doctor_name: string;
  doctor_notes: string;
  file_url: string;
  file_name: string;
  verified_at: string;
  delivered_at: string;
  source?: string;          // 'manual' | 'ocr' | 'import'
  ocr_job_id?: number;
  metrics: ReportMetric[];
  prescriptions?: ReportPrescription[];
}

interface ReportsState {
  reports: Report[];
  selectedReport: Report | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReportsState = {
  reports: [],
  selectedReport: null,
  loading: false,
  error: null,
};

export const fetchReports = createAsyncThunk(
  'reports/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.rms}/api/v1/reports`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch reports');
    }
  }
);

export const fetchReportById = createAsyncThunk(
  'reports/fetchById',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.rms}/api/v1/reports/${id}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch report');
    }
  }
);

export const createReport = createAsyncThunk(
  'reports/create',
  async (data: Partial<Report>, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.rms}/api/v1/reports`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to create report');
    }
  }
);

export const updateReport = createAsyncThunk(
  'reports/update',
  async ({ id, data }: { id: number; data: Partial<Report> }, thunkAPI) => {
    try {
      const response = await apiClient.put(`${config.services.rms}/api/v1/reports/${id}`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to update report');
    }
  }
);

export const deleteReport = createAsyncThunk(
  'reports/delete',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.rms}/api/v1/reports/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete report');
    }
  }
);

export const verifyReport = createAsyncThunk(
  'reports/verify',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.rms}/api/v1/reports/${id}/verify`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to verify report');
    }
  }
);

export const deliverReport = createAsyncThunk(
  'reports/deliver',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.rms}/api/v1/reports/${id}/deliver`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to deliver report');
    }
  }
);

export const addReportMetrics = createAsyncThunk(
  'reports/addMetrics',
  async ({ reportId, metrics }: { reportId: number; metrics: ReportMetric[] }, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.rms}/api/v1/reports/${reportId}/metrics`, { metrics });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to add metrics');
    }
  }
);

export const uploadReportFile = createAsyncThunk(
  'reports/uploadFile',
  async ({ id, file }: { id: number; file: File }, thunkAPI) => {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await apiClient.post(
        `${config.services.rms}/api/v1/reports/${id}/upload`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to upload report file');
    }
  }
);

export const downloadReportFile = createAsyncThunk(
  'reports/downloadFile',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.get(
        `${config.services.rms}/api/v1/reports/${id}/download`,
        { responseType: 'blob' }
      );
      return response.data as Blob;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to download report');
    }
  }
);

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearSelectedReport: (state) => { state.selectedReport = null; },
    clearReportsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchReports.fulfilled, (state, action) => { state.loading = false; state.reports = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchReports.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(fetchReportById.fulfilled, (state, action) => { state.selectedReport = action.payload?.data || action.payload; })
      .addCase(createReport.fulfilled, (state, action) => {
        const r = action.payload?.data || action.payload;
        if (r) state.reports.push(r);
      })
      .addCase(updateReport.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.reports.findIndex(r => r.ID === updated.ID);
          if (idx !== -1) state.reports[idx] = updated;
        }
      })
      .addCase(deleteReport.fulfilled, (state, action) => {
        state.reports = state.reports.filter(r => r.ID !== action.payload);
      })
      .addCase(verifyReport.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.reports.findIndex(r => r.ID === updated.ID);
          if (idx !== -1) state.reports[idx] = updated;
        }
      })
      .addCase(deliverReport.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.reports.findIndex(r => r.ID === updated.ID);
          if (idx !== -1) state.reports[idx] = updated;
        }
      })
      .addCase(uploadReportFile.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.reports.findIndex(r => r.ID === updated.ID);
          if (idx !== -1) state.reports[idx] = updated;
          if (state.selectedReport?.ID === updated.ID) state.selectedReport = updated;
        }
      });
  },
});

export const { clearSelectedReport, clearReportsError } = reportsSlice.actions;
export default reportsSlice.reducer;
